import {
  idSchema,
  QUESTIONNAIRE_DRAFT_CLEANUP_DAYS_DEFAULT,
  questionnaireCustomerActiveRunSchema,
  questionnaireCustomerOfflineDefinitionEnvelopeSchema,
  questionnaireCustomerOfflinePageEnvelopeSchema,
  questionnaireOfflinePartnerContextSchema,
  questionnaireOfflinePageEnvelopeSchema,
  questionnaireOfflineRunEnvelopeSchema,
  questionnaireOfflineTemplatePackageSchema,
  type QuestionnaireCustomerOfflineDefinitionEnvelope,
  type QuestionnaireCustomerOfflinePageEnvelope,
  type QuestionnaireOfflinePartnerContext,
  type QuestionnaireOfflinePageEnvelope,
  type QuestionnaireOfflineRunEnvelope,
  type QuestionnaireOfflineTemplatePackage,
} from '@erp/shared';
import type { z } from 'zod';

const DATABASE_NAME = 'erp-questionnaire-offline-v1';
const DATABASE_VERSION = 2;
const KEY_STORE = 'encryption-keys';
const DRAFT_STORE = 'drafts';
const MEDIA_STORE = 'media';
const ENVELOPE_STORE = 'envelopes';
const AES_ALGORITHM = 'AES-GCM';
const AES_KEY_LENGTH = 256;
const IV_LENGTH = 12;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1_000;
const LOCAL_RECORD_TTL_MS = QUESTIONNAIRE_DRAFT_CLEANUP_DAYS_DEFAULT * MILLISECONDS_PER_DAY;

type OfflineEnvelopeKind =
  'RUN' | 'DEFINITION' | 'PAGE' | 'CUSTOMER_PAGE' | 'CUSTOMER_DEFINITION' | 'PARTNER_CONTEXT';

export type QuestionnaireOfflineSyncState = 'PENDING' | 'SYNCING' | 'FAILED' | 'CONFLICT';

interface StoredEncryptionKey {
  readonly namespace: string;
  readonly key: CryptoKey;
  readonly createdAt: string;
}

interface StoredEncryptedValue {
  readonly storageKey: string;
  readonly namespace: string;
  /** SHA-256 locator. No run, question, partner or file identifier is stored in clear text. */
  readonly entityId: string;
  readonly iv: ArrayBuffer;
  readonly ciphertext: ArrayBuffer;
  readonly updatedAt: string;
  readonly idempotencyKey: string;
  readonly syncState: QuestionnaireOfflineSyncState;
  readonly serverRevision: number | null;
  readonly lastErrorCode: string | null;
}

interface StoredEncryptedMedia extends StoredEncryptedValue {
  /** SHA-256 of the run locator, used only to avoid decrypting unrelated blobs. */
  readonly groupKey: string;
}

interface StoredEncryptedEnvelope extends StoredEncryptedValue {
  readonly envelopeKind: OfflineEnvelopeKind;
}

interface EncryptedDraftEnvelope {
  readonly locator: string;
  readonly idempotencyKey: string;
  readonly value: unknown;
}

interface EncryptedValueEnvelope {
  readonly kind: OfflineEnvelopeKind;
  readonly locator: string;
  readonly value: unknown;
}

interface EncryptedMediaHeader {
  readonly runId: string;
  readonly mediaId: string;
  readonly idempotencyKey: string;
  readonly name: string;
  readonly mimeType: string;
  readonly size: number;
  readonly questionId: string;
  readonly productId: string | null;
  readonly description: string | null;
}

export interface QuestionnaireOfflineContext {
  readonly tenantId: string;
  readonly userId: string;
}

export interface QuestionnaireOfflineDraft<T> {
  readonly runId: string;
  readonly value: T;
  readonly updatedAt: string;
  readonly idempotencyKey: string;
  readonly syncState: QuestionnaireOfflineSyncState;
  readonly serverRevision: number | null;
  readonly lastErrorCode: string | null;
}

export interface QuestionnaireOfflineMedia {
  readonly mediaId: string;
  readonly runId: string;
  readonly questionId: string;
  readonly productId: string | null;
  readonly file: File;
  readonly description: string | null;
  readonly updatedAt: string;
  readonly idempotencyKey: string;
  readonly syncState: QuestionnaireOfflineSyncState;
  readonly lastErrorCode: string | null;
}

export interface SaveOfflineDraftOptions {
  readonly serverRevision?: number | null;
  readonly idempotencyKey?: string;
}

export interface SaveOfflineMediaOptions {
  readonly mediaId?: string;
  readonly description?: string | null;
  readonly productId?: string | undefined;
  readonly idempotencyKey?: string;
}

export interface OfflineCapability {
  readonly supported: boolean;
  readonly reason:
    | 'SUPPORTED'
    | 'INSECURE_CONTEXT'
    | 'INDEXED_DB_UNAVAILABLE'
    | 'WEB_CRYPTO_UNAVAILABLE'
    | 'KEY_STORAGE_UNAVAILABLE';
}

export interface QuestionnaireOfflineLoadResult<T> {
  readonly source: 'SERVER' | 'OFFLINE';
  readonly value: T;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener(
      'error',
      () => reject(request.error ?? new Error('IndexedDB request failed')),
      { once: true },
    );
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener('complete', () => resolve(), { once: true });
    transaction.addEventListener(
      'abort',
      () => reject(transaction.error ?? new Error('IndexedDB transaction aborted')),
      { once: true },
    );
    transaction.addEventListener(
      'error',
      () => reject(transaction.error ?? new Error('IndexedDB transaction failed')),
      { once: true },
    );
  });
}

function ensureIndex(store: IDBObjectStore, name: string, keyPath: string): void {
  if (!store.indexNames.contains(name)) store.createIndex(name, keyPath, { unique: false });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.addEventListener('upgradeneeded', () => {
      const database = request.result;
      const upgrade = request.transaction;
      if (upgrade === null) throw new Error('IndexedDB upgrade transaction is unavailable');
      if (!database.objectStoreNames.contains(KEY_STORE)) {
        database.createObjectStore(KEY_STORE, { keyPath: 'namespace' });
      }
      const drafts = database.objectStoreNames.contains(DRAFT_STORE)
        ? upgrade.objectStore(DRAFT_STORE)
        : database.createObjectStore(DRAFT_STORE, { keyPath: 'storageKey' });
      ensureIndex(drafts, 'namespace', 'namespace');
      const media = database.objectStoreNames.contains(MEDIA_STORE)
        ? upgrade.objectStore(MEDIA_STORE)
        : database.createObjectStore(MEDIA_STORE, { keyPath: 'storageKey' });
      ensureIndex(media, 'namespace', 'namespace');
      ensureIndex(media, 'entityId', 'entityId');
      ensureIndex(media, 'groupKey', 'groupKey');
      const envelopes = database.objectStoreNames.contains(ENVELOPE_STORE)
        ? upgrade.objectStore(ENVELOPE_STORE)
        : database.createObjectStore(ENVELOPE_STORE, { keyPath: 'storageKey' });
      ensureIndex(envelopes, 'namespace', 'namespace');
      ensureIndex(envelopes, 'envelopeKind', 'envelopeKind');
    });
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener(
      'error',
      () => reject(request.error ?? new Error('IndexedDB unavailable')),
      { once: true },
    );
    request.addEventListener('blocked', () => reject(new Error('IndexedDB upgrade blocked')), {
      once: true,
    });
  });
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function namespaceFor(context: QuestionnaireOfflineContext): Promise<string> {
  return sha256(`questionnaires\u0000${context.tenantId}\u0000${context.userId}`);
}

function storageKey(namespace: string, kind: string, opaque: string): string {
  return `${namespace}:${kind}:${opaque}`;
}

function customerPageLocator(
  routeRunId: string,
  sectionKey: string,
  questionKey: string | null,
): string {
  return `${routeRunId}\u0000${sectionKey}\u0000${questionKey ?? ''}`;
}

async function opaqueLocator(kind: string, locator: string): Promise<string> {
  return sha256(`${kind}\u0000${locator}`);
}

function associatedData(namespace: string, entityId: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(`${namespace}\u0000${entityId}`);
}

function encodeJson(value: unknown): ArrayBuffer {
  return new TextEncoder().encode(JSON.stringify(value)).buffer;
}

function decodeJson(value: ArrayBuffer): unknown {
  return JSON.parse(new TextDecoder().decode(value));
}

function mediaEnvelope(header: EncryptedMediaHeader, bytes: ArrayBuffer): ArrayBuffer {
  const encodedHeader = new TextEncoder().encode(JSON.stringify(header));
  const result = new Uint8Array(4 + encodedHeader.byteLength + bytes.byteLength);
  new DataView(result.buffer).setUint32(0, encodedHeader.byteLength, false);
  result.set(encodedHeader, 4);
  result.set(new Uint8Array(bytes), 4 + encodedHeader.byteLength);
  // Own the exact backing range; File/Blob implementations may expose cross-realm buffers.
  return result.buffer.slice(result.byteOffset, result.byteOffset + result.byteLength);
}

function decodedMediaEnvelope(value: ArrayBuffer): {
  readonly header: EncryptedMediaHeader;
  readonly bytes: ArrayBuffer;
} {
  if (value.byteLength < 5) throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
  const headerLength = new DataView(value).getUint32(0, false);
  if (headerLength < 2 || headerLength > value.byteLength - 4) {
    throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
  }
  const decoded: unknown = JSON.parse(
    new TextDecoder().decode(new Uint8Array(value, 4, headerLength)),
  );
  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) {
    throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
  }
  const runId = Reflect.get(decoded, 'runId');
  const mediaId = Reflect.get(decoded, 'mediaId');
  const idempotencyKey = Reflect.get(decoded, 'idempotencyKey');
  const name = Reflect.get(decoded, 'name');
  const mimeType = Reflect.get(decoded, 'mimeType');
  const size = Reflect.get(decoded, 'size');
  const questionId = Reflect.get(decoded, 'questionId');
  const productId = Reflect.get(decoded, 'productId');
  const description = Reflect.get(decoded, 'description');
  if (
    typeof runId !== 'string' ||
    typeof mediaId !== 'string' ||
    typeof idempotencyKey !== 'string' ||
    typeof name !== 'string' ||
    typeof mimeType !== 'string' ||
    typeof size !== 'number' ||
    typeof questionId !== 'string' ||
    (productId !== undefined && productId !== null && typeof productId !== 'string') ||
    (description !== null && typeof description !== 'string')
  ) {
    throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
  }
  const bytes = value.slice(4 + headerLength);
  if (bytes.byteLength !== size) throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
  return {
    header: {
      runId,
      mediaId,
      idempotencyKey,
      name,
      mimeType,
      size,
      questionId,
      productId: typeof productId === 'string' ? productId : null,
      description,
    },
    bytes,
  };
}

function decodedDraftEnvelope(value: ArrayBuffer): EncryptedDraftEnvelope {
  const decoded = decodeJson(value);
  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) {
    throw new Error('OFFLINE_DRAFT_ENVELOPE_INVALID');
  }
  const locator = Reflect.get(decoded, 'locator');
  const idempotencyKey = Reflect.get(decoded, 'idempotencyKey');
  if (
    typeof locator !== 'string' ||
    typeof idempotencyKey !== 'string' ||
    !Reflect.has(decoded, 'value')
  ) {
    throw new Error('OFFLINE_DRAFT_ENVELOPE_INVALID');
  }
  return { locator, idempotencyKey, value: Reflect.get(decoded, 'value') };
}

function decodedValueEnvelope(value: ArrayBuffer): EncryptedValueEnvelope {
  const decoded = decodeJson(value);
  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) {
    throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
  }
  const kind = Reflect.get(decoded, 'kind');
  const locator = Reflect.get(decoded, 'locator');
  if (
    (kind !== 'RUN' &&
      kind !== 'DEFINITION' &&
      kind !== 'PAGE' &&
      kind !== 'CUSTOMER_PAGE' &&
      kind !== 'CUSTOMER_DEFINITION' &&
      kind !== 'PARTNER_CONTEXT') ||
    typeof locator !== 'string' ||
    !Reflect.has(decoded, 'value')
  ) {
    throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
  }
  return { kind, locator, value: Reflect.get(decoded, 'value') };
}

async function encrypt(
  key: CryptoKey,
  namespace: string,
  entityId: string,
  plaintext: ArrayBuffer,
): Promise<{ readonly iv: ArrayBuffer; readonly ciphertext: ArrayBuffer }> {
  const ivBytes = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = await crypto.subtle.encrypt(
    { name: AES_ALGORITHM, iv: ivBytes, additionalData: associatedData(namespace, entityId) },
    key,
    new Uint8Array(plaintext),
  );
  return { iv: ivBytes.buffer, ciphertext };
}

async function decrypt(
  key: CryptoKey,
  record: Pick<StoredEncryptedValue, 'namespace' | 'entityId' | 'iv' | 'ciphertext'>,
): Promise<ArrayBuffer> {
  return crypto.subtle.decrypt(
    {
      name: AES_ALGORITHM,
      iv: new Uint8Array(record.iv),
      additionalData: associatedData(record.namespace, record.entityId),
    },
    key,
    record.ciphertext,
  );
}

async function loadOrCreateKey(database: IDBDatabase, namespace: string): Promise<CryptoKey> {
  const readTransaction = database.transaction(KEY_STORE, 'readonly');
  const existing = await requestResult(
    readTransaction.objectStore(KEY_STORE).get(namespace) as IDBRequest<
      StoredEncryptionKey | undefined
    >,
  );
  await transactionComplete(readTransaction);
  if (existing !== undefined) return existing.key;
  const key = await crypto.subtle.generateKey(
    { name: AES_ALGORITHM, length: AES_KEY_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  );
  const writeTransaction = database.transaction(KEY_STORE, 'readwrite');
  writeTransaction.objectStore(KEY_STORE).add({
    namespace,
    key,
    createdAt: new Date().toISOString(),
  } satisfies StoredEncryptionKey);
  try {
    await transactionComplete(writeTransaction);
    return key;
  } catch (error) {
    // Two hooks or tabs may initialize the same vault concurrently. The unique
    // namespace decides the winner; every loser must reload that exact key so a
    // later persist cannot replace it and make existing ciphertext unreadable.
    const retryTransaction = database.transaction(KEY_STORE, 'readonly');
    const winner = await requestResult(
      retryTransaction.objectStore(KEY_STORE).get(namespace) as IDBRequest<
        StoredEncryptionKey | undefined
      >,
    );
    await transactionComplete(retryTransaction);
    if (winner !== undefined) return winner.key;
    throw error;
  }
}

// React strict mode, multiple feature hooks and route hand-offs can open the
// same vault concurrently. Sharing the initialization promise prevents a
// same-document winner key from being deleted/re-created between two opens;
// the IndexedDB unique constraint in loadOrCreateKey remains the cross-tab
// authority.
const pendingKeyInitializations = new Map<string, Promise<CryptoKey>>();

function keyFor(database: IDBDatabase, namespace: string): Promise<CryptoKey> {
  const pending = pendingKeyInitializations.get(namespace);
  if (pending !== undefined) return pending;
  const initialization = loadOrCreateKey(database, namespace).finally(() => {
    if (pendingKeyInitializations.get(namespace) === initialization) {
      pendingKeyInitializations.delete(namespace);
    }
  });
  pendingKeyInitializations.set(namespace, initialization);
  return initialization;
}

async function recordsForNamespace<T>(
  database: IDBDatabase,
  storeName: string,
  namespace: string,
): Promise<T[]> {
  const transaction = database.transaction(storeName, 'readonly');
  const request = transaction
    .objectStore(storeName)
    .index('namespace')
    .getAll(namespace) as IDBRequest<T[]>;
  const result = await requestResult(request);
  await transactionComplete(transaction);
  return result;
}

async function clearNamespaceData(
  database: IDBDatabase,
  namespace: string,
  deleteEncryptionKey = true,
): Promise<number> {
  const [drafts, media, envelopes] = await Promise.all([
    recordsForNamespace<StoredEncryptedValue>(database, DRAFT_STORE, namespace),
    recordsForNamespace<StoredEncryptedMedia>(database, MEDIA_STORE, namespace),
    recordsForNamespace<StoredEncryptedEnvelope>(database, ENVELOPE_STORE, namespace),
  ]);
  const transaction = database.transaction(
    deleteEncryptionKey
      ? [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE, KEY_STORE]
      : [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE],
    'readwrite',
  );
  const draftStore = transaction.objectStore(DRAFT_STORE);
  drafts.forEach((record) => draftStore.delete(record.storageKey));
  const mediaStore = transaction.objectStore(MEDIA_STORE);
  media.forEach((record) => mediaStore.delete(record.storageKey));
  const envelopeStore = transaction.objectStore(ENVELOPE_STORE);
  envelopes.forEach((record) => envelopeStore.delete(record.storageKey));
  if (deleteEncryptionKey) transaction.objectStore(KEY_STORE).delete(namespace);
  await transactionComplete(transaction);
  return drafts.length + media.length + envelopes.length;
}

function mayUseOfflineFallback(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const status = Reflect.get(error, 'status');
    if (typeof status === 'number') return status === 0;
  }
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

export async function checkQuestionnaireOfflineCapability(): Promise<OfflineCapability> {
  if (!window.isSecureContext && window.location.hostname !== 'localhost') {
    return { supported: false, reason: 'INSECURE_CONTEXT' };
  }
  if (!('indexedDB' in window)) return { supported: false, reason: 'INDEXED_DB_UNAVAILABLE' };
  if (!('crypto' in window) || crypto.subtle === undefined) {
    return { supported: false, reason: 'WEB_CRYPTO_UNAVAILABLE' };
  }
  let database: IDBDatabase | null = null;
  try {
    database = await openDatabase();
    const probeNamespace = `capability-${crypto.randomUUID()}`;
    const probeKey = await crypto.subtle.generateKey(
      { name: AES_ALGORITHM, length: AES_KEY_LENGTH },
      false,
      ['encrypt', 'decrypt'],
    );
    const transaction = database.transaction(KEY_STORE, 'readwrite');
    const store = transaction.objectStore(KEY_STORE);
    store.put({
      namespace: probeNamespace,
      key: probeKey,
      createdAt: new Date().toISOString(),
    } satisfies StoredEncryptionKey);
    store.delete(probeNamespace);
    await transactionComplete(transaction);
    return { supported: true, reason: 'SUPPORTED' };
  } catch {
    return { supported: false, reason: 'KEY_STORAGE_UNAVAILABLE' };
  } finally {
    database?.close();
  }
}

const activeStoresByNamespace = new Map<string, Set<EncryptedQuestionnaireOfflineStore>>();

export class EncryptedQuestionnaireOfflineStore {
  private closed = false;
  private cleared = false;

  private constructor(
    private readonly database: IDBDatabase,
    private readonly namespace: string,
    private readonly key: CryptoKey,
    private readonly customerHandoff: boolean,
  ) {
    const activeStores = activeStoresByNamespace.get(namespace) ?? new Set();
    activeStores.add(this);
    activeStoresByNamespace.set(namespace, activeStores);
  }

  static async create(
    context: QuestionnaireOfflineContext,
  ): Promise<EncryptedQuestionnaireOfflineStore> {
    return EncryptedQuestionnaireOfflineStore.createForContext(context, false);
  }

  private static async createForContext(
    context: QuestionnaireOfflineContext,
    customerHandoff: boolean,
  ): Promise<EncryptedQuestionnaireOfflineStore> {
    const capability = await checkQuestionnaireOfflineCapability();
    if (!capability.supported) throw new Error(capability.reason);
    const database = await openDatabase();
    const namespace = await namespaceFor(context);
    const key = await keyFor(database, namespace);
    const store = new EncryptedQuestionnaireOfflineStore(database, namespace, key, customerHandoff);
    try {
      // Never delete an otherwise empty namespace key while another concurrent
      // opener may still be resolving it. Explicit logout/clearAll remains the
      // authority for key destruction.
      await store.cleanupExpiredRecords(new Date(), false);
      return store;
    } catch (error) {
      store.close();
      throw error;
    }
  }

  /**
   * Removes the authenticated user's complete questionnaire vault, including
   * its non-extractable CryptoKey, without touching another user or tenant.
   */
  static async clearContext(context: QuestionnaireOfflineContext): Promise<void> {
    const namespace = await namespaceFor(context);
    for (const store of [...(activeStoresByNamespace.get(namespace) ?? [])]) store.close();
    const database = await openDatabase();
    try {
      await clearNamespaceData(database, namespace);
    } finally {
      database.close();
    }
  }

  /**
   * Customer mode cannot call `/auth/me`. The validated route/marker run ID is
   * only an opaque local namespace locator; authorization remains the HttpOnly
   * handoff cookie whenever the server is reachable.
   */
  static createCustomerHandoff(runId: string): Promise<EncryptedQuestionnaireOfflineStore> {
    const parsedRunId = idSchema.parse(runId);
    return EncryptedQuestionnaireOfflineStore.createForContext(
      { tenantId: parsedRunId, userId: 'customer_handoff' },
      true,
    );
  }

  /**
   * Starts a customer re-handoff from a cryptographically fresh, empty vault.
   * Closing every previous handle prevents an old page write from racing the
   * server-authoritative policy decision for the new handoff.
   */
  static clearCustomerHandoff(runId: string): Promise<void> {
    const parsedRunId = idSchema.parse(runId);
    return EncryptedQuestionnaireOfflineStore.clearContext({
      tenantId: parsedRunId,
      userId: 'customer_handoff',
    });
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.database.close();
    const activeStores = activeStoresByNamespace.get(this.namespace);
    activeStores?.delete(this);
    if (activeStores?.size === 0) activeStoresByNamespace.delete(this.namespace);
  }

  private persistKey(transaction: IDBTransaction): void {
    transaction.objectStore(KEY_STORE).put({
      namespace: this.namespace,
      key: this.key,
      createdAt: new Date().toISOString(),
    } satisfies StoredEncryptionKey);
  }

  private async deleteKeyIfNamespaceEmpty(): Promise<void> {
    // A live store may immediately receive another edit. Deleting its key here
    // would let a concurrent hook create a different key and make subsequent
    // ciphertext mutually unreadable. Keys are destroyed by clearContext on
    // logout or clearAll for a completed customer handoff.
    if ((activeStoresByNamespace.get(this.namespace)?.size ?? 0) > 0) return;
    const transaction = this.database.transaction(
      [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE, KEY_STORE],
      'readwrite',
    );
    const completed = transactionComplete(transaction);
    const counts = await Promise.all(
      [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE].map((storeName) =>
        requestResult(
          transaction
            .objectStore(storeName)
            .index('namespace')
            .count(IDBKeyRange.only(this.namespace)),
        ),
      ),
    );
    if (counts.every((count) => count === 0)) {
      transaction.objectStore(KEY_STORE).delete(this.namespace);
    }
    await completed;
  }

  private async saveEnvelope<T>(input: {
    kind: OfflineEnvelopeKind;
    locator: string;
    value: T;
    idempotencyKey: string;
    syncState?: QuestionnaireOfflineSyncState | undefined;
    serverRevision?: number | null | undefined;
    lastErrorCode?: string | null | undefined;
  }): Promise<void> {
    const entityId = await opaqueLocator(input.kind, input.locator);
    const encrypted = await encrypt(
      this.key,
      this.namespace,
      entityId,
      encodeJson({ kind: input.kind, locator: input.locator, value: input.value }),
    );
    const record: StoredEncryptedEnvelope = {
      storageKey: storageKey(this.namespace, input.kind, entityId),
      namespace: this.namespace,
      entityId,
      envelopeKind: input.kind,
      ...encrypted,
      updatedAt: new Date().toISOString(),
      idempotencyKey: input.idempotencyKey,
      syncState: input.syncState ?? 'PENDING',
      serverRevision: input.serverRevision ?? null,
      lastErrorCode: input.lastErrorCode ?? null,
    };
    const transaction = this.database.transaction([ENVELOPE_STORE, KEY_STORE], 'readwrite');
    this.persistKey(transaction);
    transaction.objectStore(ENVELOPE_STORE).put(record);
    await transactionComplete(transaction);
  }

  private async loadEnvelope<TSchema extends z.ZodTypeAny>(
    kind: OfflineEnvelopeKind,
    locator: string,
    schema: TSchema,
  ): Promise<z.output<TSchema> | null> {
    const entityId = await opaqueLocator(kind, locator);
    const transaction = this.database.transaction(ENVELOPE_STORE, 'readonly');
    const record = await requestResult(
      transaction
        .objectStore(ENVELOPE_STORE)
        .get(storageKey(this.namespace, kind, entityId)) as IDBRequest<
        StoredEncryptedEnvelope | undefined
      >,
    );
    await transactionComplete(transaction);
    if (record === undefined) return null;
    const decoded = decodedValueEnvelope(await decrypt(this.key, record));
    if (decoded.kind !== kind || decoded.locator !== locator) {
      throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
    }
    return schema.parse(decoded.value);
  }

  private async listEnvelopes<TSchema extends z.ZodTypeAny>(
    kind: OfflineEnvelopeKind,
    schema: TSchema,
  ): Promise<ReadonlyArray<z.output<TSchema>>> {
    const records = await recordsForNamespace<StoredEncryptedEnvelope>(
      this.database,
      ENVELOPE_STORE,
      this.namespace,
    );
    return Promise.all(
      records
        .filter((record) => record.envelopeKind === kind)
        .map(async (record) => {
          const decoded = decodedValueEnvelope(await decrypt(this.key, record));
          if (decoded.kind !== kind) throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
          return schema.parse(decoded.value);
        }),
    );
  }

  private async removeEnvelope(kind: OfflineEnvelopeKind, locator: string): Promise<void> {
    const entityId = await opaqueLocator(kind, locator);
    const transaction = this.database.transaction(ENVELOPE_STORE, 'readwrite');
    transaction.objectStore(ENVELOPE_STORE).delete(storageKey(this.namespace, kind, entityId));
    await transactionComplete(transaction);
    await this.deleteKeyIfNamespaceEmpty();
  }

  async saveRunEnvelope(envelope: QuestionnaireOfflineRunEnvelope): Promise<void> {
    const parsed = questionnaireOfflineRunEnvelopeSchema.parse(envelope);
    await this.saveEnvelope({
      kind: 'RUN',
      locator: parsed.clientRunId,
      value: parsed,
      idempotencyKey: await opaqueLocator('RUN_IDEMPOTENCY', parsed.clientRunId),
      syncState:
        parsed.syncState === 'CONFLICT'
          ? 'CONFLICT'
          : parsed.syncState === 'FAILED'
            ? 'FAILED'
            : parsed.syncState === 'SYNCING'
              ? 'SYNCING'
              : 'PENDING',
      serverRevision: parsed.baseLockVersion,
      lastErrorCode: parsed.lastErrorCode,
    });
  }

  loadRunEnvelope(clientRunId: string): Promise<QuestionnaireOfflineRunEnvelope | null> {
    return this.loadEnvelope('RUN', clientRunId, questionnaireOfflineRunEnvelopeSchema);
  }

  listRunEnvelopes(): Promise<ReadonlyArray<QuestionnaireOfflineRunEnvelope>> {
    return this.listEnvelopes('RUN', questionnaireOfflineRunEnvelopeSchema);
  }

  async loadRunWithFallback(
    clientRunId: string,
    loadServer: () => Promise<QuestionnaireOfflineRunEnvelope>,
  ): Promise<QuestionnaireOfflineLoadResult<QuestionnaireOfflineRunEnvelope>> {
    try {
      const value = questionnaireOfflineRunEnvelopeSchema.parse(await loadServer());
      await this.saveRunEnvelope(value);
      return { source: 'SERVER', value };
    } catch (error) {
      if (!mayUseOfflineFallback(error)) throw error;
      const value = await this.loadRunEnvelope(clientRunId);
      if (value === null) throw error;
      return { source: 'OFFLINE', value };
    }
  }

  async saveTemplatePackage(offlinePackage: QuestionnaireOfflineTemplatePackage): Promise<void> {
    const parsed = questionnaireOfflineTemplatePackageSchema.parse(offlinePackage);
    await this.saveEnvelope({
      kind: 'DEFINITION',
      locator: parsed.templateVersionId,
      value: parsed,
      idempotencyKey: parsed.definitionChecksum,
    });
  }

  loadTemplatePackage(
    templateVersionId: string,
  ): Promise<QuestionnaireOfflineTemplatePackage | null> {
    return this.loadEnvelope(
      'DEFINITION',
      templateVersionId,
      questionnaireOfflineTemplatePackageSchema,
    );
  }

  listTemplatePackages(): Promise<ReadonlyArray<QuestionnaireOfflineTemplatePackage>> {
    return this.listEnvelopes('DEFINITION', questionnaireOfflineTemplatePackageSchema);
  }

  async synchronizeTemplatePackages(
    packages: readonly QuestionnaireOfflineTemplatePackage[],
  ): Promise<void> {
    const parsed = packages.map((entry) => questionnaireOfflineTemplatePackageSchema.parse(entry));
    for (const offlinePackage of parsed) await this.saveTemplatePackage(offlinePackage);
    const current = await this.listTemplatePackages();
    const retainedVersionIds = new Set(parsed.map((entry) => entry.templateVersionId));
    for (const obsolete of current.filter(
      (entry) => !retainedVersionIds.has(entry.templateVersionId),
    )) {
      await this.removeEnvelope('DEFINITION', obsolete.templateVersionId);
    }
  }

  async loadTemplatePackageWithFallback(
    templateVersionId: string,
    loadServer: () => Promise<QuestionnaireOfflineTemplatePackage>,
  ): Promise<QuestionnaireOfflineLoadResult<QuestionnaireOfflineTemplatePackage>> {
    try {
      const value = questionnaireOfflineTemplatePackageSchema.parse(await loadServer());
      await this.saveTemplatePackage(value);
      return { source: 'SERVER', value };
    } catch (error) {
      if (!mayUseOfflineFallback(error)) throw error;
      const value = await this.loadTemplatePackage(templateVersionId);
      if (value === null) throw error;
      return { source: 'OFFLINE', value };
    }
  }

  async savePageEnvelope(envelope: QuestionnaireOfflinePageEnvelope): Promise<void> {
    const parsed = questionnaireOfflinePageEnvelopeSchema.parse(envelope);
    await this.saveEnvelope({
      kind: 'PAGE',
      locator: `${parsed.clientRunId}\u0000${parsed.sectionKey}`,
      value: parsed,
      idempotencyKey: await opaqueLocator('PAGE_IDEMPOTENCY', parsed.clientRunId),
    });
  }

  loadPageEnvelope(
    clientRunId: string,
    sectionKey: string,
  ): Promise<QuestionnaireOfflinePageEnvelope | null> {
    return this.loadEnvelope(
      'PAGE',
      `${clientRunId}\u0000${sectionKey}`,
      questionnaireOfflinePageEnvelopeSchema,
    );
  }

  async loadPageWithFallback(
    clientRunId: string,
    sectionKey: string,
    loadServer: () => Promise<QuestionnaireOfflinePageEnvelope>,
  ): Promise<QuestionnaireOfflineLoadResult<QuestionnaireOfflinePageEnvelope>> {
    try {
      const value = questionnaireOfflinePageEnvelopeSchema.parse(await loadServer());
      await this.savePageEnvelope(value);
      return { source: 'SERVER', value };
    } catch (error) {
      if (!mayUseOfflineFallback(error)) throw error;
      const value = await this.loadPageEnvelope(clientRunId, sectionKey);
      if (value === null) throw error;
      return { source: 'OFFLINE', value };
    }
  }

  async saveCustomerPageEnvelope(
    envelope: QuestionnaireCustomerOfflinePageEnvelope,
  ): Promise<void> {
    const parsed = questionnaireCustomerOfflinePageEnvelopeSchema.parse(envelope);
    await this.saveEnvelope({
      kind: 'CUSTOMER_PAGE',
      locator: customerPageLocator(
        parsed.routeRunId,
        parsed.page.navigation.currentSectionKey,
        parsed.page.navigation.currentQuestionKey,
      ),
      value: parsed,
      idempotencyKey: await opaqueLocator('CUSTOMER_PAGE_IDEMPOTENCY', parsed.page.clientRunId),
      serverRevision: parsed.page.lockVersion,
    });
  }

  async saveCustomerDefinitionEnvelope(
    envelope: QuestionnaireCustomerOfflineDefinitionEnvelope,
  ): Promise<void> {
    const parsed = questionnaireCustomerOfflineDefinitionEnvelopeSchema.parse(envelope);
    await this.saveEnvelope({
      kind: 'CUSTOMER_DEFINITION',
      locator: parsed.routeRunId,
      value: parsed,
      idempotencyKey: await opaqueLocator(
        'CUSTOMER_DEFINITION_IDEMPOTENCY',
        parsed.templateVersionId,
      ),
    });
  }

  async loadCustomerDefinitionEnvelope(
    routeRunId: string,
  ): Promise<QuestionnaireCustomerOfflineDefinitionEnvelope | null> {
    const parsedRunId = idSchema.parse(routeRunId);
    const envelope = await this.loadEnvelope(
      'CUSTOMER_DEFINITION',
      parsedRunId,
      questionnaireCustomerOfflineDefinitionEnvelopeSchema,
    );
    if (envelope === null) return null;
    if (Date.parse(envelope.expiresAt) > Date.now()) return envelope;
    await this.removeEnvelope('CUSTOMER_DEFINITION', parsedRunId);
    return null;
  }

  async loadCustomerPageEnvelope(
    routeRunId: string,
    sectionKey: string,
    questionKey: string | null,
  ): Promise<QuestionnaireCustomerOfflinePageEnvelope | null> {
    const parsedRunId = idSchema.parse(routeRunId);
    const parsedSectionKey =
      questionnaireCustomerActiveRunSchema.shape.navigation.shape.currentSectionKey.parse(
        sectionKey,
      );
    const parsedQuestionKey =
      questionnaireCustomerActiveRunSchema.shape.navigation.shape.currentQuestionKey.parse(
        questionKey,
      );
    await this.removeExpiredCustomerPageEnvelopes(new Date());
    const exact = await this.loadEnvelope(
      'CUSTOMER_PAGE',
      customerPageLocator(parsedRunId, parsedSectionKey, parsedQuestionKey),
      questionnaireCustomerOfflinePageEnvelopeSchema,
    );
    if (exact !== null) return exact;

    // Version 1 used only the section key. Read it once for a compatible
    // migration, then rewrite it under the question-aware locator.
    const legacyLocator = `${parsedRunId}\u0000${parsedSectionKey}`;
    const legacy = await this.loadEnvelope(
      'CUSTOMER_PAGE',
      legacyLocator,
      questionnaireCustomerOfflinePageEnvelopeSchema,
    );
    if (
      legacy === null ||
      legacy.page.navigation.currentQuestionKey !== parsedQuestionKey ||
      Date.parse(legacy.page.expiresAt) <= Date.now()
    ) {
      return null;
    }
    await this.saveCustomerPageEnvelope(legacy);
    await this.removeEnvelope('CUSTOMER_PAGE', legacyLocator);
    return legacy;
  }

  async saveOfflinePartnerContext(context: QuestionnaireOfflinePartnerContext): Promise<void> {
    const parsed = questionnaireOfflinePartnerContextSchema.parse(context);
    await this.saveEnvelope({
      kind: 'PARTNER_CONTEXT',
      locator: parsed.partnerId,
      value: parsed,
      idempotencyKey: await opaqueLocator('PARTNER_CONTEXT_IDEMPOTENCY', parsed.partnerId),
    });
  }

  async loadOfflinePartnerContext(
    partnerId: string,
  ): Promise<QuestionnaireOfflinePartnerContext | null> {
    const parsedPartnerId = idSchema.parse(partnerId);
    const context = await this.loadEnvelope(
      'PARTNER_CONTEXT',
      parsedPartnerId,
      questionnaireOfflinePartnerContextSchema,
    );
    if (context === null) return null;
    if (Date.parse(context.cachedAt) >= Date.now() - LOCAL_RECORD_TTL_MS) return context;
    await this.removeEnvelope('PARTNER_CONTEXT', parsedPartnerId);
    return null;
  }

  async loadLatestCustomerPageEnvelope(
    routeRunId: string,
  ): Promise<QuestionnaireCustomerOfflinePageEnvelope | null> {
    const parsedRunId = idSchema.parse(routeRunId);
    const now = new Date();
    const expiredPages = await this.removeExpiredCustomerPageEnvelopes(now);
    const pages = await this.listEnvelopes(
      'CUSTOMER_PAGE',
      questionnaireCustomerOfflinePageEnvelopeSchema,
    );
    if (expiredPages > 0 && pages.length === 0 && this.customerHandoff) {
      const definitions = await this.listEnvelopes(
        'CUSTOMER_DEFINITION',
        questionnaireCustomerOfflineDefinitionEnvelopeSchema,
      );
      if (definitions.every((entry) => Date.parse(entry.expiresAt) <= now.getTime())) {
        // Do not rotate the key below a live store: another hook/tab may have
        // loaded the same non-extractable key and write immediately afterwards.
        // All customer PII is deleted; explicit completion/unlock destroys the
        // now payload-free key with clearAll.
        await clearNamespaceData(this.database, this.namespace, false);
        return null;
      }
    }
    if (expiredPages > 0) await this.deleteKeyIfNamespaceEmpty();
    const latest =
      pages
        .filter((entry) => entry.routeRunId === parsedRunId)
        .sort((left, right) => Date.parse(right.storedAt) - Date.parse(left.storedAt))[0] ?? null;
    if (latest === null || Date.parse(latest.page.expiresAt) <= now.getTime()) return null;
    return latest;
  }

  async listCustomerPageEnvelopes(
    routeRunId: string,
  ): Promise<ReadonlyArray<QuestionnaireCustomerOfflinePageEnvelope>> {
    const parsedRunId = idSchema.parse(routeRunId);
    const now = new Date();
    await this.removeExpiredCustomerPageEnvelopes(now);
    const pages = await this.listEnvelopes(
      'CUSTOMER_PAGE',
      questionnaireCustomerOfflinePageEnvelopeSchema,
    );
    return pages.filter(
      (entry) =>
        entry.routeRunId === parsedRunId && Date.parse(entry.page.expiresAt) > now.getTime(),
    );
  }

  async saveDraft<T>(
    runId: string,
    value: T,
    options: SaveOfflineDraftOptions = {},
  ): Promise<string> {
    const idempotencyKey = options.idempotencyKey ?? crypto.randomUUID();
    const entityId = await opaqueLocator('DRAFT', runId);
    const encrypted = await encrypt(
      this.key,
      this.namespace,
      entityId,
      encodeJson({ locator: runId, idempotencyKey, value }),
    );
    const record: StoredEncryptedValue = {
      storageKey: storageKey(this.namespace, 'DRAFT', entityId),
      namespace: this.namespace,
      entityId,
      ...encrypted,
      updatedAt: new Date().toISOString(),
      idempotencyKey: await opaqueLocator('DRAFT_IDEMPOTENCY', idempotencyKey),
      syncState: 'PENDING',
      serverRevision: options.serverRevision ?? null,
      lastErrorCode: null,
    };
    const transaction = this.database.transaction([DRAFT_STORE, KEY_STORE], 'readwrite');
    this.persistKey(transaction);
    transaction.objectStore(DRAFT_STORE).put(record);
    await transactionComplete(transaction);
    return idempotencyKey;
  }

  async loadDraft<TSchema extends z.ZodTypeAny>(
    runId: string,
    schema: TSchema,
  ): Promise<QuestionnaireOfflineDraft<z.output<TSchema>> | null> {
    const entityId = await opaqueLocator('DRAFT', runId);
    const transaction = this.database.transaction(DRAFT_STORE, 'readonly');
    const record = await requestResult(
      transaction
        .objectStore(DRAFT_STORE)
        .get(storageKey(this.namespace, 'DRAFT', entityId)) as IDBRequest<
        StoredEncryptedValue | undefined
      >,
    );
    await transactionComplete(transaction);
    if (record === undefined) return null;
    const decoded = decodedDraftEnvelope(await decrypt(this.key, record));
    if (decoded.locator !== runId) throw new Error('OFFLINE_DRAFT_ENVELOPE_INVALID');
    return {
      runId,
      value: schema.parse(decoded.value),
      updatedAt: record.updatedAt,
      idempotencyKey: decoded.idempotencyKey,
      syncState: record.syncState,
      serverRevision: record.serverRevision,
      lastErrorCode: record.lastErrorCode,
    };
  }

  async listDrafts<TSchema extends z.ZodTypeAny>(
    schema: TSchema,
  ): Promise<ReadonlyArray<QuestionnaireOfflineDraft<z.output<TSchema>>>> {
    const records = await recordsForNamespace<StoredEncryptedValue>(
      this.database,
      DRAFT_STORE,
      this.namespace,
    );
    return Promise.all(
      records.map(async (record) => {
        const decoded = decodedDraftEnvelope(await decrypt(this.key, record));
        return {
          runId: decoded.locator,
          value: schema.parse(decoded.value),
          updatedAt: record.updatedAt,
          idempotencyKey: decoded.idempotencyKey,
          syncState: record.syncState,
          serverRevision: record.serverRevision,
          lastErrorCode: record.lastErrorCode,
        } satisfies QuestionnaireOfflineDraft<z.output<TSchema>>;
      }),
    );
  }

  async markDraftState(
    runId: string,
    syncState: QuestionnaireOfflineSyncState,
    lastErrorCode: string | null = null,
  ): Promise<void> {
    const entityId = await opaqueLocator('DRAFT', runId);
    const key = storageKey(this.namespace, 'DRAFT', entityId);
    const transaction = this.database.transaction(DRAFT_STORE, 'readwrite');
    const store = transaction.objectStore(DRAFT_STORE);
    const record = await requestResult(
      store.get(key) as IDBRequest<StoredEncryptedValue | undefined>,
    );
    if (record !== undefined) {
      store.put({ ...record, syncState, lastErrorCode, updatedAt: new Date().toISOString() });
    }
    await transactionComplete(transaction);
  }

  async removeDraft(runId: string): Promise<void> {
    const entityId = await opaqueLocator('DRAFT', runId);
    const transaction = this.database.transaction(DRAFT_STORE, 'readwrite');
    transaction.objectStore(DRAFT_STORE).delete(storageKey(this.namespace, 'DRAFT', entityId));
    await transactionComplete(transaction);
    await this.deleteKeyIfNamespaceEmpty();
  }

  async saveMedia(
    runId: string,
    questionId: string,
    file: File,
    options: SaveOfflineMediaOptions = {},
  ): Promise<{ readonly mediaId: string; readonly idempotencyKey: string }> {
    const mediaId = options.mediaId ?? crypto.randomUUID();
    const idempotencyKey = options.idempotencyKey ?? crypto.randomUUID();
    const groupKey = await opaqueLocator('MEDIA_GROUP', runId);
    const mediaKey = await opaqueLocator('MEDIA_ITEM', mediaId);
    const entityId = `${groupKey}:${mediaKey}`;
    const encrypted = await encrypt(
      this.key,
      this.namespace,
      entityId,
      mediaEnvelope(
        {
          runId,
          mediaId,
          idempotencyKey,
          name: file.name,
          mimeType: file.type,
          size: file.size,
          questionId,
          productId: options.productId ?? null,
          description: options.description ?? null,
        },
        await file.arrayBuffer(),
      ),
    );
    const record: StoredEncryptedMedia = {
      storageKey: storageKey(this.namespace, 'MEDIA', entityId),
      namespace: this.namespace,
      entityId,
      groupKey,
      ...encrypted,
      updatedAt: new Date().toISOString(),
      idempotencyKey: await opaqueLocator('MEDIA_IDEMPOTENCY', idempotencyKey),
      syncState: 'PENDING',
      serverRevision: null,
      lastErrorCode: null,
    };
    const transaction = this.database.transaction([MEDIA_STORE, KEY_STORE], 'readwrite');
    this.persistKey(transaction);
    transaction.objectStore(MEDIA_STORE).put(record);
    await transactionComplete(transaction);
    return { mediaId, idempotencyKey };
  }

  async listMedia(runId: string): Promise<ReadonlyArray<QuestionnaireOfflineMedia>> {
    const groupKey = await opaqueLocator('MEDIA_GROUP', runId);
    const records = await recordsForNamespace<StoredEncryptedMedia>(
      this.database,
      MEDIA_STORE,
      this.namespace,
    );
    return Promise.all(
      records
        .filter((record) => record.groupKey === groupKey)
        .map(async (record) => {
          const plaintext = decodedMediaEnvelope(await decrypt(this.key, record));
          if (plaintext.header.runId !== runId) throw new Error('OFFLINE_MEDIA_ENVELOPE_INVALID');
          return {
            mediaId: plaintext.header.mediaId,
            runId,
            questionId: plaintext.header.questionId,
            productId: plaintext.header.productId,
            file: new File([plaintext.bytes], plaintext.header.name, {
              type: plaintext.header.mimeType,
              lastModified: Date.parse(record.updatedAt),
            }),
            description: plaintext.header.description,
            updatedAt: record.updatedAt,
            idempotencyKey: plaintext.header.idempotencyKey,
            syncState: record.syncState,
            lastErrorCode: record.lastErrorCode,
          } satisfies QuestionnaireOfflineMedia;
        }),
    );
  }

  async markMediaState(
    runId: string,
    mediaId: string,
    syncState: QuestionnaireOfflineSyncState,
    lastErrorCode: string | null = null,
  ): Promise<void> {
    const groupKey = await opaqueLocator('MEDIA_GROUP', runId);
    const mediaKey = await opaqueLocator('MEDIA_ITEM', mediaId);
    const key = storageKey(this.namespace, 'MEDIA', `${groupKey}:${mediaKey}`);
    const transaction = this.database.transaction(MEDIA_STORE, 'readwrite');
    const store = transaction.objectStore(MEDIA_STORE);
    const record = await requestResult(
      store.get(key) as IDBRequest<StoredEncryptedMedia | undefined>,
    );
    if (record !== undefined) {
      store.put({ ...record, syncState, lastErrorCode, updatedAt: new Date().toISOString() });
    }
    await transactionComplete(transaction);
  }

  async removeMedia(runId: string, mediaId: string): Promise<boolean> {
    const groupKey = await opaqueLocator('MEDIA_GROUP', runId);
    const mediaKey = await opaqueLocator('MEDIA_ITEM', mediaId);
    const storageLocator = storageKey(this.namespace, 'MEDIA', `${groupKey}:${mediaKey}`);
    const transaction = this.database.transaction(MEDIA_STORE, 'readwrite');
    const store = transaction.objectStore(MEDIA_STORE);
    const existing = await requestResult(
      store.get(storageLocator) as IDBRequest<StoredEncryptedMedia | undefined>,
    );
    if (existing !== undefined) store.delete(storageLocator);
    await transactionComplete(transaction);
    await this.deleteKeyIfNamespaceEmpty();
    return existing !== undefined;
  }

  async clearRun(runId: string): Promise<void> {
    const media = await this.listMedia(runId);
    const draftEntityId = await opaqueLocator('DRAFT', runId);
    const groupKey = await opaqueLocator('MEDIA_GROUP', runId);
    const mediaStorageKeys = await Promise.all(
      media.map(async (entry) => {
        const mediaKey = await opaqueLocator('MEDIA_ITEM', entry.mediaId);
        return storageKey(this.namespace, 'MEDIA', `${groupKey}:${mediaKey}`);
      }),
    );
    const transaction = this.database.transaction([DRAFT_STORE, MEDIA_STORE], 'readwrite');
    transaction.objectStore(DRAFT_STORE).delete(storageKey(this.namespace, 'DRAFT', draftEntityId));
    const mediaStore = transaction.objectStore(MEDIA_STORE);
    mediaStorageKeys.forEach((key) => mediaStore.delete(key));
    await transactionComplete(transaction);
    await this.deleteKeyIfNamespaceEmpty();
  }

  /** Removes every run/page/blob after a confirmed sync while retaining reusable template packages. */
  async clearSynchronizedRun(input: {
    clientRunId: string;
    serverRunId?: string | undefined;
  }): Promise<void> {
    if (this.cleared) return;
    if (this.closed) {
      if (this.customerHandoff) {
        await this.clearAll();
        return;
      }
      throw new Error('OFFLINE_STORE_CLOSED');
    }
    const run = await this.loadRunEnvelope(input.clientRunId);
    const pages = await this.listEnvelopes('PAGE', questionnaireOfflinePageEnvelopeSchema);
    const customerPages = await this.listEnvelopes(
      'CUSTOMER_PAGE',
      questionnaireCustomerOfflinePageEnvelopeSchema,
    );
    const locators = new Set([
      input.clientRunId,
      ...(input.serverRunId === undefined ? [] : [input.serverRunId]),
      ...(run?.serverRunId === null || run?.serverRunId === undefined ? [] : [run.serverRunId]),
    ]);
    for (const locator of locators) await this.clearRun(locator);
    await this.removeEnvelope('RUN', input.clientRunId);
    for (const page of pages.filter((entry) => entry.clientRunId === input.clientRunId)) {
      await this.removeEnvelope('PAGE', `${page.clientRunId}\u0000${page.sectionKey}`);
    }
    for (const page of customerPages.filter(
      (entry) =>
        entry.page.clientRunId === input.clientRunId ||
        entry.routeRunId === input.serverRunId ||
        entry.routeRunId === run?.serverRunId,
    )) {
      await this.removeEnvelope(
        'CUSTOMER_PAGE',
        customerPageLocator(
          page.routeRunId,
          page.page.navigation.currentSectionKey,
          page.page.navigation.currentQuestionKey,
        ),
      );
      await this.removeEnvelope(
        'CUSTOMER_PAGE',
        `${page.routeRunId}\u0000${page.page.navigation.currentSectionKey}`,
      );
    }
    for (const routeRunId of [input.serverRunId, run?.serverRunId].filter(
      (value): value is string => value !== undefined && value !== null,
    )) {
      await this.removeEnvelope('CUSTOMER_DEFINITION', routeRunId);
    }
    if (this.customerHandoff) await this.clearAll();
  }

  /** Removes obsolete encrypted records and prevents orphaned local Blob accumulation. */
  async cleanupOlderThan(cutoff: Date): Promise<number> {
    const stores = [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE] as const;
    let removed = 0;
    for (const storeName of stores) {
      const records = await recordsForNamespace<StoredEncryptedValue>(
        this.database,
        storeName,
        this.namespace,
      );
      const expired = records.filter((record) => Date.parse(record.updatedAt) < cutoff.getTime());
      if (expired.length === 0) continue;
      const transaction = this.database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      expired.forEach((record) => store.delete(record.storageKey));
      await transactionComplete(transaction);
      removed += expired.length;
    }
    await this.deleteKeyIfNamespaceEmpty();
    return removed;
  }

  /**
   * Applies the local privacy TTL whenever a vault is opened. Customer pages
   * use their server-issued expiry; employee drafts and blobs use the central
   * stale-draft retention period.
   */
  async cleanupExpiredRecords(now: Date = new Date(), deleteEmptyKey = true): Promise<number> {
    const cutoff = now.getTime() - LOCAL_RECORD_TTL_MS;
    const [drafts, media, envelopes] = await Promise.all([
      recordsForNamespace<StoredEncryptedValue>(this.database, DRAFT_STORE, this.namespace),
      recordsForNamespace<StoredEncryptedMedia>(this.database, MEDIA_STORE, this.namespace),
      recordsForNamespace<StoredEncryptedEnvelope>(this.database, ENVELOPE_STORE, this.namespace),
    ]);
    const isExpired = (record: StoredEncryptedValue): boolean => {
      const updatedAt = Date.parse(record.updatedAt);
      return !Number.isFinite(updatedAt) || updatedAt < cutoff;
    };
    const expiredDrafts = drafts.filter(isExpired);
    const expiredMedia = media.filter(isExpired);
    const expiringEnvelopeKinds = new Set<OfflineEnvelopeKind>(['RUN', 'PAGE', 'PARTNER_CONTEXT']);
    const expiredEnvelopes = envelopes.filter(
      (record) => expiringEnvelopeKinds.has(record.envelopeKind) && isExpired(record),
    );
    let removed = expiredDrafts.length + expiredMedia.length + expiredEnvelopes.length;
    if (removed > 0) {
      const transaction = this.database.transaction(
        [DRAFT_STORE, MEDIA_STORE, ENVELOPE_STORE],
        'readwrite',
      );
      const draftStore = transaction.objectStore(DRAFT_STORE);
      expiredDrafts.forEach((record) => draftStore.delete(record.storageKey));
      const mediaStore = transaction.objectStore(MEDIA_STORE);
      expiredMedia.forEach((record) => mediaStore.delete(record.storageKey));
      const envelopeStore = transaction.objectStore(ENVELOPE_STORE);
      expiredEnvelopes.forEach((record) => envelopeStore.delete(record.storageKey));
      await transactionComplete(transaction);
    }
    removed += await this.removeExpiredCustomerPageEnvelopes(now);
    removed += await this.removeExpiredCustomerDefinitionEnvelopes(now);
    if (this.customerHandoff) {
      const [validCustomerPages, validCustomerDefinitions] = await Promise.all([
        this.listEnvelopes('CUSTOMER_PAGE', questionnaireCustomerOfflinePageEnvelopeSchema),
        this.listEnvelopes(
          'CUSTOMER_DEFINITION',
          questionnaireCustomerOfflineDefinitionEnvelopeSchema,
        ),
      ]);
      if (validCustomerPages.length === 0 && validCustomerDefinitions.length === 0) {
        // Customer drafts and blobs must never outlive the short server-issued
        // handoff expiry. Initialization deliberately retains only the
        // non-extractable key to prevent a concurrent live store from being
        // stranded with a different key generation.
        removed += await clearNamespaceData(this.database, this.namespace, false);
        return removed;
      }
    }
    if (deleteEmptyKey) await this.deleteKeyIfNamespaceEmpty();
    return removed;
  }

  private async removeExpiredCustomerPageEnvelopes(now: Date): Promise<number> {
    const records = await recordsForNamespace<StoredEncryptedEnvelope>(
      this.database,
      ENVELOPE_STORE,
      this.namespace,
    );
    const expiredStorageKeys: string[] = [];
    for (const record of records.filter((entry) => entry.envelopeKind === 'CUSTOMER_PAGE')) {
      const decoded = decodedValueEnvelope(await decrypt(this.key, record));
      if (decoded.kind !== 'CUSTOMER_PAGE') throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
      const envelope = questionnaireCustomerOfflinePageEnvelopeSchema.parse(decoded.value);
      if (Date.parse(envelope.page.expiresAt) <= now.getTime()) {
        expiredStorageKeys.push(record.storageKey);
      }
    }
    if (expiredStorageKeys.length === 0) return 0;
    const transaction = this.database.transaction(ENVELOPE_STORE, 'readwrite');
    const store = transaction.objectStore(ENVELOPE_STORE);
    expiredStorageKeys.forEach((key) => store.delete(key));
    await transactionComplete(transaction);
    return expiredStorageKeys.length;
  }

  private async removeExpiredCustomerDefinitionEnvelopes(now: Date): Promise<number> {
    const records = await recordsForNamespace<StoredEncryptedEnvelope>(
      this.database,
      ENVELOPE_STORE,
      this.namespace,
    );
    const expiredStorageKeys: string[] = [];
    for (const record of records.filter((entry) => entry.envelopeKind === 'CUSTOMER_DEFINITION')) {
      const decoded = decodedValueEnvelope(await decrypt(this.key, record));
      if (decoded.kind !== 'CUSTOMER_DEFINITION') {
        throw new Error('OFFLINE_VALUE_ENVELOPE_INVALID');
      }
      const envelope = questionnaireCustomerOfflineDefinitionEnvelopeSchema.parse(decoded.value);
      if (Date.parse(envelope.expiresAt) <= now.getTime()) {
        expiredStorageKeys.push(record.storageKey);
      }
    }
    if (expiredStorageKeys.length === 0) return 0;
    const transaction = this.database.transaction(ENVELOPE_STORE, 'readwrite');
    const store = transaction.objectStore(ENVELOPE_STORE);
    expiredStorageKeys.forEach((key) => store.delete(key));
    await transactionComplete(transaction);
    return expiredStorageKeys.length;
  }

  async clearAll(): Promise<void> {
    if (this.cleared) return;
    if (this.closed) {
      const database = await openDatabase();
      try {
        await clearNamespaceData(database, this.namespace);
      } finally {
        database.close();
      }
      this.cleared = true;
      return;
    }
    await clearNamespaceData(this.database, this.namespace);
    this.cleared = true;
    this.close();
  }
}
